import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreateUserRequest, UpdateUserRequest, UserDetailResponse } from '../../api/generated';
import { Table } from '../UI';
import { Button } from '../UI/base/Button';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { useUsers } from '../../hooks/useUsers';
import { useLocations } from '../../hooks/useLocations';
import { useAppDispatch } from '../../store/hooks';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { getErrorMessage } from '../../utils/errorMessageUtils';
import { getUserColumns } from './userColumns';
import { UserFormModal } from './UserFormModal';
import type { UserFormValues } from './UserFormModal';
import { DeleteUserModal } from './DeleteUserModal';
import styles from './UsersSettings.module.css';

const toLocationPermissions = (values: UserFormValues): CreateUserRequest['locationPermissions'] =>
  values.locationPermissions
    .filter((p) => p.read || p.write || p.patientBooker)
    .map((p) => ({
      locationId: p.locationId,
      read: p.read,
      write: p.write,
      patientBooker: p.patientBooker
    }));

export const UsersSettings = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { users, isLoading, loadError, createUser, updateUser, deleteUser } = useUsers();
  const { locations } = useLocations();

  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserDetailResponse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserDetailResponse | null>(null);

  const columns = useMemo(
    () =>
      getUserColumns(t, {
        onEdit: (user) => {
          setEditingUser(user);
          setFormOpen(true);
        },
        onDelete: (user) => setDeleteTarget(user)
      }),
    [t]
  );

  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingUser(null);
  };

  const handleSave = async (values: UserFormValues) => {
    const target = editingUser;
    const payload: CreateUserRequest | UpdateUserRequest = {
      email: values.email.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      accountAdmin: values.accountAdmin,
      locationPermissions: toLocationPermissions(values)
    };
    try {
      if (target) {
        await updateUser(target.userId, payload);
      } else {
        await createUser(payload);
      }
      handleCloseForm();
    } catch (e) {
      const fallback = target ? t('config-user-update-error') : t('config-user-create-error');
      dispatch(showError({ message: getErrorMessage(e, fallback) }));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUser(deleteTarget.userId);
      setDeleteTarget(null);
    } catch (e) {
      dispatch(showError({ message: getErrorMessage(e, t('config-user-delete-error')) }));
    }
  };

  const showEmptyState = !isLoading && !loadError && users.length === 0;

  return (
    <section>
      <SectionHeader title={t('config-tab-users')}>
        <Button type="primary" onClick={handleOpenCreate}>
          {t('config-user-add')}
        </Button>
      </SectionHeader>

      {isLoading && users.length === 0 && (
        <p className={styles.emptyState}>{t('config-user-loading')}</p>
      )}

      {loadError && <p className={styles.emptyState}>{t('config-user-load-error')}</p>}

      {showEmptyState && <p className={styles.emptyState}>{t('config-user-empty')}</p>}

      {users.length > 0 && (
        <Table columns={columns} data={users} getRowKey={(u) => u.userId} minWidth={620} />
      )}

      {formOpen && (
        <UserFormModal
          user={editingUser}
          locations={locations}
          onSave={handleSave}
          onClose={handleCloseForm}
        />
      )}

      {deleteTarget && (
        <DeleteUserModal
          userName={`${deleteTarget.firstName} ${deleteTarget.lastName}`.trim()}
          onConfirm={handleConfirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </section>
  );
};
